/**
 * Scope constants and label dictionaries.
 *
 * These deliberately avoid importing `@prisma/client` so that `@cwy/config`
 * stays at the very bottom of the dependency graph and can be consumed by the
 * browser bundle through the `./contracts` entry point.
 */

/**
 * Boards and grades are DATA — rows in `CurriculumBoard` / `CurriculumGrade`,
 * added by a platform operator — not a list fixed in code. A board or grade is
 * therefore just its code here, and the live list comes from the API
 * (`GET /curriculum/levels`), never from a constant.
 *
 * What stays fixed is the FORMAT of a code, and that a code never changes once
 * used: every curriculum key, and all content tagged with one, is built from it.
 */
export type Board = string;
export type Grade = string;

/** A board or grade code: upper-case, then letters, digits or underscores. */
export const LEVEL_CODE_RE = /^[A-Z][A-Z0-9_]{0,23}$/;

/**
 * The boards and grades CWY launched with — the seed's starting set, and the
 * codes the first migration filled the tables with. Read by the seed and tests
 * only; anything shown to a person reads the tables.
 */
export const SEED_BOARDS = ['ICSE', 'CBSE'] as const;
export const SEED_GRADES = ['TEN', 'TWELVE'] as const;

export const BIN_COLORS = ['GREEN', 'PURPLE', 'BLUE', 'ORANGE', 'PINK'] as const;
export type BinColor = (typeof BIN_COLORS)[number];

// ── Tenancy ─────────────────────────────────────────────────────────────────
//
// The old `ROLES = ['STUDENT','TEACHER','ADMIN']` answered two different
// questions with one value, which is why `role === 'ADMIN'` became an all-data
// bypass: there was no way to say "administers exactly one school". Platform
// authority and tenant authority are separate ladders and nothing maps between
// them — a SCHOOL_ADMIN is not a lesser PLATFORM_ADMIN.

export const PLATFORM_ROLES = ['USER', 'PLATFORM_ADMIN', 'PLATFORM_SUPER_ADMIN'] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export const MEMBERSHIP_ROLES = ['SCHOOL_ADMIN', 'COUNSELLOR', 'TEACHER', 'STUDENT'] as const;
export type MembershipRole = (typeof MEMBERSHIP_ROLES)[number];

export const MEMBERSHIP_STATUSES = ['INVITED', 'ACTIVE', 'SUSPENDED', 'LEFT'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

export const TENANT_TYPES = ['FIRST_PARTY', 'SCHOOL'] as const;
export type TenantType = (typeof TENANT_TYPES)[number];

export const TENANT_STATUSES = ['ACTIVE', 'SUSPENDED', 'ARCHIVED'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

/**
 * The shortest password anyone may choose, everywhere.
 *
 * One number, because the places that enforce it are not one place: the Zod
 * contract, Better Auth's own check, and the copy on three different forms. When
 * the B2C sign-up said eight and the staff and roster flows said ten, a school
 * admin could hand out a password their own student could not have chosen.
 *
 * Length only — see `zPassword` for why composition rules are not added on top.
 */
export const PASSWORD_MIN_LENGTH = 8;

export const CREDENTIAL_MODES = ['SELF_MANAGED', 'TENANT_MANAGED'] as const;
export type CredentialMode = (typeof CREDENTIAL_MODES)[number];

export const SIGNUP_POLICIES = [
  'CLOSED',
  'ROSTER_ACTIVATION',
  'INVITE_CODE',
  'OPEN_APPROVAL',
] as const;
export type SignupPolicy = (typeof SIGNUP_POLICIES)[number];

export const LOGIN_IDENTIFIER_TYPES = [
  'EMAIL',
  'USERNAME',
  'ADMISSION_NUMBER',
  'ROLL_NUMBER',
] as const;
export type LoginIdentifierType = (typeof LOGIN_IDENTIFIER_TYPES)[number];

/** Which login surface a URL selects. A surface is a hint, never an authority. */
export const LOGIN_SURFACES = ['STUDENT', 'TEACHER', 'ADMIN'] as const;
export type LoginSurface = (typeof LOGIN_SURFACES)[number];

/** Human labels for the role indicator in the app chrome. */
export const MEMBERSHIP_ROLE_LABEL: Record<MembershipRole, string> = {
  SCHOOL_ADMIN: 'School admin',
  COUNSELLOR: 'Counsellor',
  TEACHER: 'Teacher',
  STUDENT: 'Student',
};

export const PLATFORM_ROLE_LABEL: Record<PlatformRole, string> = {
  USER: 'User',
  PLATFORM_ADMIN: 'Platform admin',
  PLATFORM_SUPER_ADMIN: 'Super admin',
};

/**
 * The one slug that identifies CWY's own B2C workspace.
 *
 * Deliberately a single constant rather than a value repeated in the seed, the
 * resolver and the web bootstrap. The first-party tenant is resolved by this
 * slug everywhere; a second copy is the thing that drifts.
 */
export const FIRST_PARTY_TENANT_SLUG = 'cwy';

/**
 * Slugs a school can never claim, because each already means something at the
 * root of the URL space or would shadow a platform surface.
 */
export const RESERVED_TENANT_SLUGS: readonly string[] = [
  FIRST_PARTY_TENANT_SLUG,
  'admin',
  'api',
  'app',
  'auth',
  'login',
  'signup',
  'school',
  'teacher',
  'student',
  'www',
  'mail',
  'static',
  'assets',
  'support',
  'help',
  'status',
];

/**
 * The header carrying the tenant hint from browser to API.
 *
 * The web app and the API may sit on different origins, so the path alone
 * cannot travel with the request. This is a HINT: middleware resolves it to a
 * tenant, then validates the caller's membership and every resource's tenant
 * foreign key. It is never trusted as authority on its own.
 */
export const TENANT_HINT_HEADER = 'x-cwy-tenant-slug';

/**
 * Domain for synthetic addresses belonging to students who have no email.
 *
 * `.invalid` is reserved by RFC 2606 and can never resolve, which is the point:
 * Better Auth requires a unique email per user, but a roster student may only
 * ever have an admission number. These addresses are an authentication-adapter
 * detail — never shown as the student's email, never a delivery target, and
 * never sent a verification or reset message.
 */
export const INTERNAL_EMAIL_DOMAIN = 'students.cwy.invalid';

/** Is this a synthetic address rather than a real, reachable mailbox? */
export function isInternalEmail(email: string | null | undefined): boolean {
  return !!email && email.toLowerCase().endsWith(`@${INTERNAL_EMAIL_DOMAIN}`);
}

/** The two grades that predate grade codes of the `G<n>` form. */
const LEGACY_GRADE_NUMBER: Readonly<Record<string, string>> = { TEN: '10', TWELVE: '12' };

/**
 * A grade as keys, prompts and headings write it: "10" for TEN, "9" for G9,
 * and the lower-cased code for a grade with no number ("kg").
 *
 * Load-bearing for `curriculumKey()`: every existing key was built with "10" and
 * "12", so this must keep returning exactly that for TEN and TWELVE. Creating a
 * grade is refused when its `gradeLabel` matches an existing grade's (G10 next
 * to TEN), or two grades would build the same keys.
 */
export function gradeLabel(grade: Grade): string {
  const legacy = LEGACY_GRADE_NUMBER[grade];
  if (legacy) return legacy;
  const numbered = /^G(\d{1,2})$/.exec(grade);
  return numbered ? String(Number(numbered[1])) : grade.toLowerCase();
}

/**
 * Normalize a school name into a candidate URL slug.
 *
 * Replaces slugifySchool(), and the difference is the whole point of this
 * migration: that function produced an authorization key from text a student
 * typed, so spelling a school's name correctly joined it. This produces only a
 * SUGGESTION for a slug an operator then confirms; the resulting Tenant.id — a
 * foreign key — is what authorizes anything.
 */
export function slugifyTenantName(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const slug = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.length > 0 ? slug.slice(0, 63) : null;
}

/**
 * Is this the SHAPE of a tenant slug? Says nothing about whether it is free.
 *
 * Bounded to DNS label rules because the same slug has to work as
 * `:slug.domain.com` once subdomain routing is enabled — a slug that is legal
 * in a path but illegal in a hostname would resolve through one URL form and
 * 404 through the other.
 *
 * This is the predicate every LOOKUP wants. Reserved names are excluded from
 * being CLAIMED, not from existing: `cwy` is itself a reserved name AND the
 * first-party workspace's real slug, so validating a lookup with the
 * assignment rule made `GET /tenant?slug=cwy` answer 404 for a row that was
 * present and ACTIVE — every root-domain login surface dead, with a healthy
 * database and a healthy boot.
 */
export function isWellFormedTenantSlug(slug: string): boolean {
  if (!/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])?$/.test(slug)) return false;
  return !slug.includes('--');
}

/**
 * The tenant slug a hostname implies, for the `:slug.domain.com` form.
 *
 * ONE implementation, shared by the API's tenant middleware and the browser,
 * because the two have to agree exactly: the client sends the slug it derives
 * as a hint header, and the API REJECTS a request whose host and header
 * disagree. Two implementations meant two answers — the API measured against a
 * configured root domain while the browser counted labels, so at
 * `cwy.getidev.in` the browser derived a tenant named "cwy" that the API knew
 * nothing about, and every root-domain login surface broke.
 *
 * `primaryHost` is the platform's ROOT domain and must be configured, never
 * inferred: a hostname cannot tell you where the platform's domain ends and a
 * tenant label begins. Empty means subdomain routing is not configured, and no
 * hostname yields a slug — the safe default, since the `/school/:slug` path
 * form is what ships today and a wrong guess breaks every surface at once.
 *
 * Returns null for the root domain itself, for `www` and `app`, and for
 * anything deeper than a single label. Callers exclude their own service
 * hostnames (the API excludes the origin it is served from) before calling.
 */
export function tenantSlugFromHostname(hostname: string, primaryHost: string): string | null {
  const primary = primaryHost.trim().toLowerCase();
  if (!primary) return null;

  const host = hostname.trim().toLowerCase();
  if (host === primary || host === `www.${primary}`) return null;
  if (!host.endsWith(`.${primary}`)) return null;

  const label = host.slice(0, -(primary.length + 1));
  // Only a single label is a tenant subdomain. `a.b.domain.com` is not.
  if (label.includes('.')) return null;
  if (label === 'www' || label === 'app') return null;

  // A label that could never be a slug is not a tenant hint — it is noise, and
  // treating it as a hint would 404 the request instead of falling through to
  // the path form.
  return isWellFormedTenantSlug(label) ? label : null;
}

/**
 * May a NEW tenant claim this slug?
 *
 * Shape, plus the reserved list. Use this only where a slug is being assigned;
 * see `isWellFormedTenantSlug` for the question every resolver is actually
 * asking.
 */
export function isValidTenantSlug(slug: string): boolean {
  if (!isWellFormedTenantSlug(slug)) return false;
  return !RESERVED_TENANT_SLUGS.includes(slug);
}

/**
 * Normalize a school-local login identifier for storage and lookup.
 *
 * THIS FUNCTION IS THE UNIQUENESS RULE. It is called once when a student is
 * created and once again on every sign-in, and if those two ever computed
 * different answers a student who was provisioned successfully could never log
 * in — a failure that surfaces as "wrong password" and is close to
 * undiagnosable from the outside. That is why it lives here, at the bottom of
 * the dependency graph, rather than next to either caller.
 *
 * Unicode-normalizes (so a composed and a decomposed "ñ" are one identifier),
 * strips invisible and control characters (which would otherwise let two
 * visually identical roll numbers coexist), collapses internal whitespace and
 * case-folds. Returns null for anything empty after that.
 */
export function normalizeLoginIdentifier(raw: string | undefined | null): string | null {
  if (!raw) return null;
  const cleaned = raw
    .normalize('NFKC')
    // C0/C1 controls, zero-width joiners/spaces, BOM, and the bidi overrides —
    // all invisible, all able to disguise one identifier as another.
    //
    // Matching control characters is the entire purpose here: they must be
    // stripped before an identifier is compared, or two visually identical roll
    // numbers can coexist and one student can be locked out by another's.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u202A-\u202E\u2060\uFEFF]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
  return cleaned.length > 0 ? cleaned.slice(0, 128) : null;
}

/**
 * Which identifier type a raw student login string looks like.
 *
 * Only used to label the stored row for display and reporting. Lookup never
 * branches on type — it matches the normalized value within the tenant — so a
 * misclassification here cannot lock anyone out.
 */
export function classifyLoginIdentifier(raw: string): LoginIdentifierType {
  if (raw.includes('@')) return 'EMAIL';
  if (/^[0-9]+$/.test(raw.trim())) return 'ADMISSION_NUMBER';
  return 'USERNAME';
}

// ── Timezone helpers ────────────────────────────────────────────────────────
//
// The product is India-only, and streaks, daily study plans and notification
// dedupe keys all pivot on the IST calendar day. The old codebase had two
// divergent implementations of this (Intl-based in routes/garden.ts, offset
// arithmetic in jobs/studyPlan.ts); this is the single canonical one.

export const IST_TIMEZONE = 'Asia/Kolkata';

/** `YYYY-MM-DD` for the IST calendar day containing `at`. */
export function istDayKey(at: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(at);
}

/** Days since the epoch in IST — cheap adjacency test for streak arithmetic. */
export function istDayNumber(at: Date = new Date()): number {
  const [year, month, day] = istDayKey(at).split('-').map(Number);
  return Math.floor(Date.UTC(year, month - 1, day) / 86_400_000);
}

/** ISO-8601 week key (`2026-W33`) for weekly revision sheets. */
export function isoWeekKey(at: Date = new Date()): string {
  const date = new Date(Date.UTC(at.getUTCFullYear(), at.getUTCMonth(), at.getUTCDate()));
  // ISO weeks run Monday–Sunday and are numbered by the Thursday they contain.
  const dayOfWeek = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayOfWeek);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
